// Médicos y personal. Centro Medico → Clínica → Médicos.
// El médico es un partner (tipo 'medico') + extensión profesional (medical.medico).
// Alta/edición vía RPC med_guardar_medico. Horarios y ausencias vía REST.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarOff, Clock, KeyRound, Link2, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  AutoTextarea,
  ArchivoUpload, Ausencia, Campo, Especialidad, Horario, ImageUpload, MedicoRow, Pais, Switch, TelefonoInput, fieldCls,
  DIAS, TIPO_ATENCION, TIPO_AUSENCIA, med, medRpc,
} from './shared';

const vacio = {
  razon_social: '', identificacion: '', tipo_identificacion: 'cedula', email: '', telefono: '', celular: '',
  nacionalidad: '',
  especialidad_id: '', numero_licencia: '', registro_acess: '', titulo: '',
  foto_url: '', firma_url: '', firma_modo: 'manual', logo_url: '', precio_consulta: 0, duracion_consulta: 30, acepta_telematica: false,
  observaciones: '', es_activo: true,
};
type Form = typeof vacio;

interface Detalle { medico: Record<string, unknown>; partner: Record<string, unknown>; horarios: Horario[]; ausencias: Ausencia[] }

const TABS = [
  ['personales', 'Datos personales'], ['contacto', 'Datos de Contacto'],
  ['profesional', 'Perfil profesional'], ['horarios', 'Horarios'],
] as const;
type TabKey = typeof TABS[number][0];

export function Medicos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('');

  const { data: rows = [], isFetching } = useQuery<MedicoRow[]>({
    queryKey: ['med-medicos', filtro],
    queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: filtro }),
  });
  const { data: especialidades = [] } = useQuery<Especialidad[]>({
    queryKey: ['med-especialidades-sel'],
    queryFn: () => med.list<Especialidad>('especialidad', '?select=id,nombre&deleted_at=is.null&es_activo=is.true&order=nombre'),
  });
  const { data: paises = [] } = useQuery<Pais[]>({ queryKey: ['med-paises'], queryFn: () => medRpc<Pais[]>('med_paises'), staleTime: Infinity });

  const [abierto, setAbierto] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(vacio);
  const [tab, setTab] = useState<TabKey>('personales');
  const set = (k: keyof Form, v: string | boolean | number) => setForm((f) => ({ ...f, [k]: v }));

  const abrirNuevo = () => { setEditId(null); setPartnerId(null); setForm(vacio); setTab('personales'); setAbierto(true); };

  const abrirEditar = useMutation({
    mutationFn: (id: string) => medRpc<Detalle>('med_medico', { _id: id }),
    onSuccess: (d) => {
      const p = d.partner as Record<string, string | null>;
      const m = d.medico as Record<string, unknown>;
      setEditId(m.id as string); setPartnerId(p.id as string);
      setForm({
        razon_social: (p.razon_social as string) ?? '', identificacion: p.identificacion ?? '',
        tipo_identificacion: p.tipo_identificacion ?? 'cedula', email: p.email ?? '',
        telefono: p.telefono ?? '', celular: p.celular ?? '', nacionalidad: p.nacionalidad ?? '',
        especialidad_id: (m.especialidad_id as string) ?? '', numero_licencia: (m.numero_licencia as string) ?? '',
        registro_acess: (m.registro_acess as string) ?? '', titulo: (m.titulo as string) ?? '',
        foto_url: (m.foto_url as string) ?? '', firma_url: (m.firma_url as string) ?? '', firma_modo: (m.firma_modo as string) ?? 'electronica', logo_url: (m.logo_url as string) ?? '',
        precio_consulta: Number(m.precio_consulta ?? 0), duracion_consulta: Number(m.duracion_consulta ?? 30),
        acepta_telematica: (m.acepta_telematica as boolean) ?? false, observaciones: (m.observaciones as string) ?? '',
        es_activo: (m.es_activo as boolean) ?? true,
      });
      setTab('personales');
      setAbierto(true);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardar = useMutation({
    mutationFn: () => medRpc<Detalle>('med_guardar_medico', {
      _data: { ...form, especialidad_id: form.especialidad_id || null, id: editId ?? undefined, partner_id: partnerId ?? undefined },
    }),
    onSuccess: (d) => {
      toast.success('Médico guardado');
      const m = d.medico as Record<string, string>; const p = d.partner as Record<string, string>;
      setEditId(m.id); setPartnerId(p.id);
      void qc.invalidateQueries({ queryKey: ['med-medicos'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => med.update('medico', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => { toast.success('Médico eliminado'); void qc.invalidateQueries({ queryKey: ['med-medicos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      {abierto ? (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setAbierto(false)}><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
          <h1 className="text-xl font-semibold">{editId ? 'Editar médico' : 'Nuevo médico'}</h1>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Médicos y personal</h1>
          <Button onClick={abrirNuevo}><Plus className="w-4 h-4 mr-1" /> Nuevo médico</Button>
        </div>
      )}

      {!abierto && (<>
      <form className="flex gap-2 max-w-xl" onSubmit={(e) => { e.preventDefault(); setFiltro(busqueda.trim()); }}>
        <input className={fieldCls} placeholder="Buscar por nombre, cédula o licencia…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        <Button type="submit" variant="outline"><Search className="w-4 h-4" /></Button>
      </form>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Médico</th><th className="py-2 pr-3">Especialidad</th><th className="py-2 pr-3">Licencia</th>
              <th className="py-2 pr-3">Consulta</th><th className="py-2 pr-3">Rating</th><th className="py-2 pr-3">Telemática</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin médicos</td></tr>}
              {rows.map((m) => (
                <tr key={m.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{m.razon_social}</td>
                  <td className="py-2 pr-3">{m.especialidad || '—'}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{m.numero_licencia || '—'}</td>
                  <td className="py-2 pr-3">${Number(m.precio_consulta).toFixed(2)}</td>
                  <td className="py-2 pr-3">{m.n_calificaciones ? `★ ${m.rating} (${m.n_calificaciones})` : '—'}</td>
                  <td className="py-2 pr-3">{m.acepta_telematica ? 'Sí' : 'No'}</td>
                  <td className="py-2 pr-3">{m.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" aria-label="Editar" onClick={() => abrirEditar.mutate(m.id)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" aria-label="Eliminar"
                      onClick={async () => { if (!(await dlg.confirmar(`¿Eliminar a "${m.razon_social}"?`, { peligro: true }))) return; eliminar.mutate(m.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      </>)}

      {abierto && (
        <Card>
          <CardContent className="pt-4">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-1 border-b">
              {TABS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setTab(k)}
                  className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === k ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                  {l}
                </button>
              ))}
            </div>

            {tab === 'personales' && (
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Nombre completo *"><input className={fieldCls} value={form.razon_social} onChange={(e) => set('razon_social', e.target.value)} /></Campo>
                <Campo label="Identificación *"><input className={fieldCls} value={form.identificacion} onChange={(e) => set('identificacion', e.target.value)} /></Campo>
                <Campo label="Tipo de identificación">
                  <select className={fieldCls} value={form.tipo_identificacion} onChange={(e) => set('tipo_identificacion', e.target.value)}>
                    <option value="cedula">Cédula</option><option value="ruc">RUC</option><option value="pasaporte">Pasaporte</option>
                  </select>
                </Campo>
                <Campo label="Nacionalidad">
                  <select className={fieldCls} value={form.nacionalidad} onChange={(e) => set('nacionalidad', e.target.value)}>
                    <option value="">—</option>{paises.map((p) => <option key={p.codigo} value={p.nacionalidad}>{p.nacionalidad}</option>)}
                  </select>
                </Campo>
              </div>
            )}

            {tab === 'contacto' && (
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Teléfono"><TelefonoInput value={form.telefono} onChange={(v) => set('telefono', v)} paises={paises} /></Campo>
                <Campo label="Celular"><TelefonoInput value={form.celular} onChange={(v) => set('celular', v)} paises={paises} /></Campo>
                <Campo label="Email" className="col-span-2"><input className={fieldCls} value={form.email} onChange={(e) => set('email', e.target.value)} /></Campo>
              </div>
            )}

            {tab === 'profesional' && (
              <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Especialidad">
                  <select className={fieldCls} value={form.especialidad_id} onChange={(e) => set('especialidad_id', e.target.value)}>
                    <option value="">—</option>{especialidades.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                  </select>
                </Campo>
                <Campo label="Título"><input className={fieldCls} value={form.titulo} onChange={(e) => set('titulo', e.target.value)} /></Campo>
                <Campo label="Nº de licencia"><input className={fieldCls} value={form.numero_licencia} onChange={(e) => set('numero_licencia', e.target.value)} /></Campo>
                <Campo label="Registro ACESS/SENESCYT"><input className={fieldCls} value={form.registro_acess} onChange={(e) => set('registro_acess', e.target.value)} /></Campo>
                <Campo label="Precio consulta ($)"><input type="number" step="0.01" min={0} className={fieldCls} value={form.precio_consulta} onChange={(e) => set('precio_consulta', Number(e.target.value))} /></Campo>
                <Campo label="Duración consulta (min)"><input type="number" min={5} className={fieldCls} value={form.duracion_consulta} onChange={(e) => set('duracion_consulta', Number(e.target.value))} /></Campo>
                <ImageUpload label="Fotografía" value={form.foto_url} onChange={(v) => set('foto_url', v)} />
                <ImageUpload label="Rúbrica (imagen — se imprime en firma MANUAL)" value={form.firma_url} onChange={(v) => set('firma_url', v)} ancho />
                <Campo label="Firma en impresos (default)">
                  <select className={fieldCls} value={form.firma_modo} onChange={(e) => set('firma_modo', e.target.value)}>
                    <option value="electronica">Electrónica (.p12 — PDF firmado + QR de verificación)</option>
                    <option value="manual">Manual (imprime la imagen de la rúbrica; sin imagen, línea en blanco)</option>
                  </select>
                </Campo>
                {form.firma_modo === 'electronica' && (
                  <div className="col-span-2">
                    {editId
                      ? <FirmaP12Medico medicoId={editId} nombre={`${form.titulo ? form.titulo + ' ' : ''}${form.razon_social}`.trim()} />
                      : <p className="text-xs text-muted-foreground">Guarda el médico para configurar su certificado .p12.</p>}
                  </div>
                )}
                <ImageUpload label="Logo personal (impresión de HC)" value={form.logo_url} onChange={(v) => set('logo_url', v)} ancho />
                <Campo label="Observaciones" className="col-span-2"><AutoTextarea value={form.observaciones} onChange={(e) => set('observaciones', e.target.value)} /></Campo>
                <div className="col-span-2"><Switch checked={form.acepta_telematica} onChange={(v) => set('acepta_telematica', v)} label="Acepta consulta telemática" /></div>
              </div>
              {editId
                ? <Titulos medicoId={editId} />
                : <p className="text-xs text-muted-foreground border-t pt-3">Guarda el médico para agregar sus títulos.</p>}
              {editId && <AccesoMedico medicoId={editId} sugerido={form.identificacion} email={form.email} />}
              </div>
            )}

            {tab === 'horarios' && (
              editId
                ? <div className="space-y-4"><Horarios medicoId={editId} /><Ausencias medicoId={editId} /></div>
                : <p className="text-xs text-muted-foreground">Guarda el médico para configurar sus horarios y ausencias.</p>
            )}

            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set('es_activo', v)} label="Activo" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cerrar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.razon_social.trim() || !form.identificacion.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---- Acceso / login del médico (usuario ligado al partner del médico) ----
// Certificado .p12 del MÉDICO para firmar recetas: vive en autenticacion.usuario_firmas
// del USUARIO vinculado al médico (mismo almacén que los contratos del CRM). El archivo
// se guarda en base64 y la clave cifrada write-only (usuario_firma_set, solo nivel 5).
function FirmaP12Medico({ medicoId, nombre }: { medicoId: string; nombre: string }) {
  const qc = useQueryClient();
  const [p12b64, setP12b64] = useState('');
  const [p12nombre, setP12nombre] = useState('');
  const [clave, setClave] = useState('');
  const { data: acceso, isFetching } = useQuery<{ usuario_id: string; username: string; firma_electronica: boolean } | null>({
    queryKey: ['med-medico-acceso-firma', medicoId],
    queryFn: () => medRpc('med_medico_acceso', { _medico_id: medicoId }),
    staleTime: 0,
  });
  const guardar = useMutation({
    mutationFn: async () => {
      await apiClient.post('/rpc/usuario_firma_set', {
        _usuario: acceso!.usuario_id, _cert_b64: p12b64 || null, _password: clave || null,
        _firmante: nombre || null, _activo: true,
      });
    },
    onSuccess: () => {
      toast.success('Certificado .p12 guardado — las próximas recetas saldrán firmadas electrónicamente');
      setP12b64(''); setP12nombre(''); setClave('');
      void qc.invalidateQueries({ queryKey: ['med-medico-acceso-firma', medicoId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const leerP12 = (f: File) => {
    if (f.size > 512 * 1024) { toast.error('El .p12 supera 512 KB'); return; }
    const r = new FileReader();
    r.onload = () => { const s = String(r.result); setP12b64(s.slice(s.indexOf(',') + 1)); setP12nombre(f.name); };
    r.onerror = () => toast.error('No se pudo leer el archivo');
    r.readAsDataURL(f);
  };
  if (isFetching && !acceso) return <p className="text-xs text-muted-foreground">Cargando firma…</p>;
  if (!acceso) {
    return <p className="text-xs text-amber-600">⚠ El médico no tiene <b>usuario de acceso</b> — créalo abajo (Acceso al sistema) y luego sube aquí su certificado .p12. La firma electrónica es personal: se firma con la identidad del médico.</p>;
  }
  return (
    <div className="rounded-md border p-3 space-y-2 bg-muted/20">
      <div className="flex items-center gap-2 text-sm">
        <KeyRound className="w-4 h-4 text-primary" />
        <span className="font-medium">Certificado de firma electrónica (.p12)</span>
        {acceso.firma_electronica
          ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">configurado ✓</span>
          : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">sin configurar</span>}
        <span className="text-xs text-muted-foreground ml-auto">usuario: {acceso.username}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 items-end">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted">
          {p12nombre || 'Elegir archivo .p12…'}
          <input type="file" accept=".p12,.pfx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) leerP12(f); e.target.value = ''; }} />
        </label>
        <input type="password" className={fieldCls} placeholder={acceso.firma_electronica ? 'Clave (en blanco = no cambiar)' : 'Clave del certificado'} value={clave} onChange={(e) => setClave(e.target.value)} autoComplete="new-password" />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-muted-foreground">El archivo y la clave se guardan cifrados; la clave nunca vuelve a mostrarse. Al emitir una receta, el sistema genera el PDF y lo firma con este certificado (QR de verificación en el impreso).</p>
        <Button size="sm" variant="outline" className="shrink-0 ml-2" isLoading={guardar.isPending}
          disabled={!p12b64 && !clave} onClick={() => guardar.mutate()}>Guardar firma</Button>
      </div>
    </div>
  );
}

function AccesoMedico({ medicoId, sugerido, email }: { medicoId: string; sugerido: string; email: string }) {
  const qc = useQueryClient();
  const [modo, setModo] = useState<null | 'crear' | 'vincular'>(null);
  const [f, setF] = useState({ username: '', email: '', password: '' });
  const [login, setLogin] = useState('');
  const invalidar = () => void qc.invalidateQueries({ queryKey: ['med-medico-acceso', medicoId] });
  const { data: acceso } = useQuery<{ username: string; email: string | null; activo: boolean } | null>({
    queryKey: ['med-medico-acceso', medicoId],
    queryFn: () => medRpc<{ username: string; email: string | null; activo: boolean } | null>('med_medico_acceso', { _medico_id: medicoId }),
  });
  const crear = useMutation({
    mutationFn: () => medRpc('med_crear_acceso_medico', { _medico_id: medicoId, _username: f.username, _email: f.email || null, _password: f.password }),
    onSuccess: () => { toast.success('Acceso del médico creado'); setModo(null); setF({ username: '', email: '', password: '' }); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const vincular = useMutation({
    mutationFn: () => medRpc('med_vincular_usuario_medico', { _medico_id: medicoId, _login: login }),
    onSuccess: () => { toast.success('Usuario existente vinculado al médico'); setModo(null); setLogin(''); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  if (acceso) {
    return (
      <Seccion titulo="Acceso del médico">
        <div className="text-sm">Vinculado al usuario <b>{acceso.username}</b>{acceso.activo ? '' : ' (inactivo)'} — el médico entra con ese login.</div>
      </Seccion>
    );
  }
  if (modo === null) {
    return (
      <Seccion titulo="Acceso del médico">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => { setModo('crear'); setF({ username: sugerido || '', email: email || '', password: '' }); }}>
            <KeyRound className="w-4 h-4 mr-1" /> Crear acceso nuevo
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setModo('vincular'); setLogin(''); }}>
            <Link2 className="w-4 h-4 mr-1" /> Vincular usuario existente
          </Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">El vínculo se hace por el partner del médico (usuarios.partner_id = medico.partner_id).</p>
      </Seccion>
    );
  }
  if (modo === 'vincular') {
    return (
      <Seccion titulo="Acceso del médico · vincular existente">
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-6"><Campo label="Usuario o email existente *"><input className={fieldCls} value={login} onChange={(e) => setLogin(e.target.value)} placeholder="username o email del usuario ya creado" /></Campo></div>
          <div className="col-span-6 flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setModo(null)}>Cancelar</Button>
            <Button size="sm" isLoading={vincular.isPending} disabled={!login.trim()} onClick={() => vincular.mutate()}>Vincular</Button>
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Asocia ese usuario a la ficha del médico. Debe volver a iniciar sesión para que tome efecto.</p>
      </Seccion>
    );
  }
  return (
    <Seccion titulo="Acceso del médico · crear nuevo">
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-4"><Campo label="Usuario *"><input className={fieldCls} value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></Campo></div>
        <div className="col-span-4"><Campo label="Email"><input className={fieldCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Campo></div>
        <div className="col-span-4"><Campo label="Contraseña *"><input type="password" className={fieldCls} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Campo></div>
        <div className="col-span-12 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setModo(null)}>Cancelar</Button>
          <Button size="sm" isLoading={crear.isPending} disabled={!f.username || !f.password} onClick={() => crear.mutate()}>Crear acceso</Button>
        </div>
      </div>
    </Seccion>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return <section className="space-y-2"><h4 className="text-sm font-semibold text-muted-foreground">{titulo}</h4>{children}</section>;
}

// ---- Horarios (plantilla semanal) ----
const horVacio = { dia_semana: 1, hora_inicio: '08:00', hora_fin: '12:00', tipo_atencion: 'presencial', consultorio: '' };

function Horarios({ medicoId }: { medicoId: string }) {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery<Horario[]>({
    queryKey: ['med-horarios', medicoId],
    queryFn: () => med.list<Horario>('medico_horario', `?medico_id=eq.${medicoId}&deleted_at=is.null&order=dia_semana,hora_inicio`),
  });
  const [h, setH] = useState<typeof horVacio>(horVacio);

  const agregar = useMutation({
    mutationFn: () => med.create('medico_horario', { ...h, medico_id: medicoId, consultorio: h.consultorio || null }),
    onSuccess: () => { setH(horVacio); void qc.invalidateQueries({ queryKey: ['med-horarios', medicoId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitar = useMutation({
    mutationFn: (id: string) => med.update('medico_horario', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['med-horarios', medicoId] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Seccion titulo="Horarios de atención">
      {rows.length > 0 && (
        <ul className="text-sm divide-y border rounded-md">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between px-3 py-1.5">
              <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-muted-foreground" />
                {DIAS[r.dia_semana]} · {r.hora_inicio?.slice(0, 5)}–{r.hora_fin?.slice(0, 5)} · <span className="text-muted-foreground">{r.tipo_atencion}</span>{r.consultorio && ` · ${r.consultorio}`}</span>
              <button className="text-red-500 hover:text-urgente" title="Quitar" aria-label="Quitar" onClick={() => quitar.mutate(r.id)}><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-3"><Campo label="Día">
          <select className={fieldCls} value={h.dia_semana} onChange={(e) => setH({ ...h, dia_semana: Number(e.target.value) })}>
            {DIAS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select></Campo></div>
        <div className="col-span-2"><Campo label="Desde"><input type="time" className={fieldCls} value={h.hora_inicio} onChange={(e) => setH({ ...h, hora_inicio: e.target.value })} /></Campo></div>
        <div className="col-span-2"><Campo label="Hasta"><input type="time" className={fieldCls} value={h.hora_fin} onChange={(e) => setH({ ...h, hora_fin: e.target.value })} /></Campo></div>
        <div className="col-span-3"><Campo label="Atención">
          <select className={fieldCls} value={h.tipo_atencion} onChange={(e) => setH({ ...h, tipo_atencion: e.target.value })}>
            {TIPO_ATENCION.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></Campo></div>
        <div className="col-span-2"><Campo label="Consultorio"><input className={fieldCls} value={h.consultorio} onChange={(e) => setH({ ...h, consultorio: e.target.value })} /></Campo></div>
        <div className="col-span-12"><Button size="sm" variant="outline" isLoading={agregar.isPending} onClick={() => agregar.mutate()}><Plus className="w-4 h-4 mr-1" /> Agregar horario</Button></div>
      </div>
    </Seccion>
  );
}

// ---- Ausencias ----
const ausVacio = { tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' };

function Ausencias({ medicoId }: { medicoId: string }) {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery<Ausencia[]>({
    queryKey: ['med-ausencias', medicoId],
    queryFn: () => med.list<Ausencia>('medico_ausencia', `?medico_id=eq.${medicoId}&deleted_at=is.null&order=fecha_inicio.desc`),
  });
  const [a, setA] = useState<typeof ausVacio>(ausVacio);

  const agregar = useMutation({
    mutationFn: () => med.create('medico_ausencia', { ...a, medico_id: medicoId, motivo: a.motivo || null }),
    onSuccess: () => { setA(ausVacio); void qc.invalidateQueries({ queryKey: ['med-ausencias', medicoId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitar = useMutation({
    mutationFn: (id: string) => med.update('medico_ausencia', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['med-ausencias', medicoId] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Seccion titulo="Ausencias / vacaciones">
      {rows.length > 0 && (
        <ul className="text-sm divide-y border rounded-md">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between px-3 py-1.5">
              <span className="flex items-center gap-2"><CalendarOff className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="capitalize">{r.tipo}</span> · {r.fecha_inicio} → {r.fecha_fin}{r.motivo && <span className="text-muted-foreground"> · {r.motivo}</span>}</span>
              <button className="text-red-500 hover:text-urgente" title="Quitar" aria-label="Quitar" onClick={() => quitar.mutate(r.id)}><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-3"><Campo label="Tipo">
          <select className={fieldCls} value={a.tipo} onChange={(e) => setA({ ...a, tipo: e.target.value })}>
            {TIPO_AUSENCIA.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></Campo></div>
        <div className="col-span-3"><Campo label="Desde"><input type="date" className={fieldCls} value={a.fecha_inicio} onChange={(e) => setA({ ...a, fecha_inicio: e.target.value })} /></Campo></div>
        <div className="col-span-3"><Campo label="Hasta"><input type="date" className={fieldCls} value={a.fecha_fin} onChange={(e) => setA({ ...a, fecha_fin: e.target.value })} /></Campo></div>
        <div className="col-span-3"><Campo label="Motivo"><input className={fieldCls} value={a.motivo} onChange={(e) => setA({ ...a, motivo: e.target.value })} /></Campo></div>
        <div className="col-span-12"><Button size="sm" variant="outline" isLoading={agregar.isPending} disabled={!a.fecha_inicio || !a.fecha_fin} onClick={() => agregar.mutate()}><Plus className="w-4 h-4 mr-1" /> Agregar ausencia</Button></div>
      </div>
    </Seccion>
  );
}

// ---- Títulos / formación (varios por médico) ----
interface TituloRow {
  id: string; titulo: string; institucion: string | null; tipo: string;
  numero_registro: string | null; fecha_registro: string | null; reconocimiento: string | null; pdf_url: string | null;
}
const tituloVacio = { titulo: '', institucion: '', tipo: 'nacional', numero_registro: '', fecha_registro: '', reconocimiento: '', pdf_url: '' };

function Titulos({ medicoId }: { medicoId: string }) {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery<TituloRow[]>({
    queryKey: ['med-titulos', medicoId],
    queryFn: () => med.list<TituloRow>('medico_titulo', `?medico_id=eq.${medicoId}&deleted_at=is.null&order=created_at`),
  });
  const [t, setT] = useState<typeof tituloVacio>(tituloVacio);

  const agregar = useMutation({
    mutationFn: () => med.create('medico_titulo', {
      ...t, medico_id: medicoId, institucion: t.institucion || null, numero_registro: t.numero_registro || null,
      fecha_registro: t.fecha_registro || null, reconocimiento: t.reconocimiento || null, pdf_url: t.pdf_url || null,
    }),
    onSuccess: () => { setT(tituloVacio); void qc.invalidateQueries({ queryKey: ['med-titulos', medicoId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitar = useMutation({
    mutationFn: (id: string) => med.update('medico_titulo', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['med-titulos', medicoId] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Seccion titulo="Títulos / formación">
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                <th className="py-2 px-2">Título</th><th className="py-2 px-2">Institución</th><th className="py-2 px-2">Tipo</th>
                <th className="py-2 px-2">Nº registro</th><th className="py-2 px-2">Fecha</th><th className="py-2 px-2">Reconocimiento</th>
                <th className="py-2 px-2">PDF</th><th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-1.5 px-2">{r.titulo}</td>
                  <td className="py-1.5 px-2">{r.institucion || '—'}</td>
                  <td className="py-1.5 px-2 capitalize">{r.tipo}</td>
                  <td className="py-1.5 px-2">{r.numero_registro || '—'}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{r.fecha_registro || '—'}</td>
                  <td className="py-1.5 px-2">{r.reconocimiento || '—'}</td>
                  <td className="py-1.5 px-2">{r.pdf_url ? <a href={r.pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">Ver</a> : '—'}</td>
                  <td className="py-1.5 px-2 text-right"><button className="text-red-500 hover:text-urgente" title="Quitar" aria-label="Quitar" onClick={() => quitar.mutate(r.id)}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-3"><Campo label="Título"><input className={fieldCls} value={t.titulo} onChange={(e) => setT({ ...t, titulo: e.target.value })} /></Campo></div>
        <div className="col-span-3"><Campo label="Institución"><input className={fieldCls} value={t.institucion} onChange={(e) => setT({ ...t, institucion: e.target.value })} /></Campo></div>
        <div className="col-span-2"><Campo label="Tipo">
          <select className={fieldCls} value={t.tipo} onChange={(e) => setT({ ...t, tipo: e.target.value })}>
            <option value="nacional">Nacional</option><option value="internacional">Internacional</option>
          </select></Campo></div>
        <div className="col-span-2"><Campo label="Nº registro"><input className={fieldCls} value={t.numero_registro} onChange={(e) => setT({ ...t, numero_registro: e.target.value })} /></Campo></div>
        <div className="col-span-2"><Campo label="Fecha registro"><input type="date" className={fieldCls} value={t.fecha_registro} onChange={(e) => setT({ ...t, fecha_registro: e.target.value })} /></Campo></div>
        <div className="col-span-6"><Campo label="Reconocimiento"><input className={fieldCls} value={t.reconocimiento} onChange={(e) => setT({ ...t, reconocimiento: e.target.value })} /></Campo></div>
        <div className="col-span-6"><Campo label="PDF del título"><ArchivoUpload value={t.pdf_url} onChange={(v) => setT({ ...t, pdf_url: v })} /></Campo></div>
        <div className="col-span-12">
          <Button size="sm" variant="outline" isLoading={agregar.isPending} disabled={!t.titulo.trim()} onClick={() => agregar.mutate()}>
            <Plus className="w-4 h-4 mr-1" /> Agregar título
          </Button>
        </div>
      </div>
    </Seccion>
  );
}
